import MUIDataTable, {
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  MUIDataTableMeta,
} from 'mui-datatables';
import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';

import {
  Grid,
  IconButton,
  Button,
  Tooltip,
} from '@material-ui/core';
import { Refresh, Edit, Delete } from '@material-ui/icons';

import MUITableLoading from 'components/MUITableLoading/MUITableLoading';
import { tags as displayDefaultTags } from '../../components/DisplayTag/tags';
import Loading from '../../components/Loading';
import PageError from '../../components/PageError';
import PageTitle from '../../components/PageTitle/PageTitle';
import { Toast } from '../../components/Toast';
import { GET_TAGS } from '../../queries/tag';
import { ITag } from '../../typings/tag';
import CreateTagModal from './components/CreateTagModal';
import DeleteTagModal from './components/DeleteTagModal';
import EditTagModal from './components/EditTagModal';
import useStyles from './styles';
import Layout from '../../components/Layout/Layout';

interface ITags {
  id: string;
  name: string;
  permanent: boolean;
}

const TagsPage: React.FC = () => {
  const [tags, setTags] = useState<ITags[]>([]);
  const [editModalIsOpened, setEditModalIsOpened] = useState(false);
  const [createModalIsOpened, setCreateModalIsOpened] = useState(false);
  const [
    deleteConfirmationModalIsOpened,
    setDeleteConfirmationModalIsOpened,
  ] = useState(false);
  const [selectedTag, setSelectedTag] = useState<ITag | undefined>();
  const [openLoading, setOpenLoading] = useState(false);

  const classes = useStyles();

  const {
    loading: tagsContentLoading,
    error: tagsContentError,
    data: tagsContentData,
    refetch: tagsContentRefetch,
  } = useQuery(GET_TAGS, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (tagsContentData) {
      const defaultTags: ITags[] = Object.keys(displayDefaultTags).map((defaultTag, index) => ({
        id: `${index}`,
        name: defaultTag,
        permanent: true,
      }));
      setTags([
        ...defaultTags,
        ...tagsContentData.listTags,
      ]);
    }
  }, [tagsContentData]);

  const openEditModal = (tag: ITag) => {
    setEditModalIsOpened(true);
    setSelectedTag(tag);
  };

  const closeEditModal = () => {
    setEditModalIsOpened(false);
  };

  const closeCreateModal = () => {
    setCreateModalIsOpened(false);
  };

  const openDeleteConfirmationModal = (tag: ITag) => {
    setDeleteConfirmationModalIsOpened(true);
    setSelectedTag(tag);
  };

  const closeDeleteConfirmationModal = () => {
    setDeleteConfirmationModalIsOpened(false);
  };

  const refetchTags = async (showBackdrop = true) => {
    if (showBackdrop) setOpenLoading(true);
    tagsContentRefetch();

    if (showBackdrop) {
      setOpenLoading(false);
      Toast('Titles updated successfully!');
    }
  };

  const refreshList = () => (
    <Tooltip title="Refresh tags">
      <IconButton aria-label="refresh" onClick={() => refetchTags()}>
        <Refresh />
      </IconButton>
    </Tooltip>
  );

  const bodyMutableTags = (_: any, tableMeta: MUIDataTableMeta) => {
    const [id, name, permanent] = tableMeta.rowData;
    const tag = {
      id,
      name,
      permanent,
    };

    if (permanent) return false;

    return (
      <>
        <IconButton onClick={() => openEditModal(tag)}>
          <Edit />
        </IconButton>
        <IconButton onClick={() => openDeleteConfirmationModal(tag)}>
          <Delete />
        </IconButton>
      </>
    );
  };

  const columns: MUIDataTableColumnDef[] = [
    {
      name: 'id',
      options: {
        display: false,
        filter: false,
      },
    },
    {
      name: 'name',
      label: 'NAME',
      options: {
        filter: false,
      },
    },
    {
      name: 'permanent',
      label: 'STATUS',
      options: {
        display: true,
        customBodyRender: (value: boolean) => {
          if (value) return 'permanent';
          return 'editable';
        },
      },
    },
    {
      name: 'actions',
      label: 'ACTIONS',
      options: {
        filter: false,
        customBodyRender: bodyMutableTags,
      },
    },
  ];

  const options: MUIDataTableOptions = {
    selectableRowsHeader: false,
    selectableRows: 'none',
    count: tags.length,
  };

  return (
    <Layout>
      <>
        <Loading openLoading={openLoading} />
        <Grid container spacing={0}>
          <Grid item xs={10}>
            <PageTitle title="Tags" />
          </Grid>
          <Grid item xs={2}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={() => setCreateModalIsOpened(true)}
            >
              Add
            </Button>
          </Grid>
          <Grid item xs={12} className={classes.grid}>
            {tagsContentLoading ? <MUITableLoading withRefresh selectable="none" columns={['NAME', 'STATUS', 'ACTIONS']} /> : tagsContentError ? (
              <PageError />
            ) : (
              <MUIDataTable
                title={refreshList()}
                data={tags}
                columns={columns}
                options={options}
              />
            )}
          </Grid>
          {editModalIsOpened && (
            <EditTagModal
              isOpened={editModalIsOpened}
              handleClose={closeEditModal}
              tag={selectedTag}
              refetchList={tagsContentRefetch}
            />
          )}
          {createModalIsOpened && (
            <CreateTagModal
              isOpened={createModalIsOpened}
              handleClose={closeCreateModal}
              refetchList={tagsContentRefetch}
            />
          )}
          {deleteConfirmationModalIsOpened && (
            <DeleteTagModal
              isOpened={deleteConfirmationModalIsOpened}
              tag={selectedTag}
              handleClose={closeDeleteConfirmationModal}
              refetchList={tagsContentRefetch}
            />
          )}
        </Grid>
      </>
    </Layout>
  );
};

export default TagsPage;
